import {
  AGGREGATION_GRADE,
  AGGREGATION_STUDENT,
  AGGREGATION_TEACHER,
  BASE_ENTITY_LABEL,
  ResultRow,
  SelectedGradeOption,
  SelectedTeacherOption,
  SkillTag,
  buildGradePlaceholders,
  buildPlaceholderRow,
  buildTeacherPlaceholders,
  makeGetSortableEntityName,
  supplementRowsWithPlaceholders,
} from '../../adminMomentumReports/helpers'

// Shared logic is re-exported so consumers and tests have one import point per report.
export * from '../../adminMomentumReports/helpers'

export const AGGREGATION_STANDARD = 'standard'

export const groupByDropdownOptions = [
  { label: 'Standard', value: AGGREGATION_STANDARD },
  { label: 'Grade', value: AGGREGATION_GRADE },
  { label: 'Teacher', value: AGGREGATION_TEACHER },
  { label: 'Student', value: AGGREGATION_STUDENT },
]

export const ENTITY_LABEL = {
  [AGGREGATION_STANDARD]: { singular: 'standard', plural: 'standards' },
  ...BASE_ENTITY_LABEL,
}

export interface StateStandardsFramework {
  id: number;
  abbreviation: string;
  name: string;
}

export interface StateStandard {
  id: number;
  code: string;
  description: string;
  grades: string[];
  skills: SkillTag[];
}

export interface SkillCategory {
  id: number;
  name: string;
  grades?: string[];
}

export const ALL_GRADE_LEVELS_OPTION = { label: 'All grades', value: 'all' }

export const ADMIN_STANDARDS_REPORT_MOMENTUM_SCORE_GOAL = 'adminStandardsReportMomentumScoreGoal'

export const SHOW_MORE_THRESHOLDS: Record<string, number> = {
  [AGGREGATION_STANDARD]: 3,
  [AGGREGATION_TEACHER]: 4,
  [AGGREGATION_STUDENT]: 4,
}

export const getSortableEntityName = makeGetSortableEntityName(AGGREGATION_STANDARD)

// The query names by-standard rows with the bare standard id; attach catalog metadata and drop rows whose standard isn't in the catalog.
export const enrichStandardRows = (rows: ResultRow[], allStateStandards: StateStandard[]): ResultRow[] => {
  const standardsById = Object.fromEntries(allStateStandards.map((standard) => [String(standard.id), standard]))
  return rows.reduce((acc, row) => {
    const standard = standardsById[String(row.aggregate_id)]
    if (!standard) return acc
    acc.push({
      ...row,
      name: standard.code,
      code: standard.code,
      description: standard.description,
      skills: standard.skills,
      grades: standard.grades,
    })
    return acc
  }, [] as ResultRow[])
}

interface SupplementMissingRowsOpts {
  rows: ResultRow[];
  aggregation: string;
  allStateStandards: StateStandard[];
  standardGradeLevel: string | null;
  selectedGrades?: SelectedGradeOption[] | null;
  selectedTeachers?: SelectedTeacherOption[] | null;
}

// Unpracticed standards only surface for a specific Standard Grade Level; with
// "All grades" the expected set can't be enumerated.
const buildStandardPlaceholders = ({ rows, allStateStandards, standardGradeLevel }: SupplementMissingRowsOpts): ResultRow[] => {
  if (!standardGradeLevel) return []
  const expected = allStateStandards.filter((standard) => standard.grades?.includes(standardGradeLevel))
  const presentIds = new Set(rows.map((row) => String(row.aggregate_id)))
  return expected
    .filter((standard) => !presentIds.has(String(standard.id)))
    .map((standard) => buildPlaceholderRow(standard.id, standard.code, AGGREGATION_STANDARD, {
      code: standard.code,
      description: standard.description,
      skills: standard.skills,
      grades: standard.grades,
    }))
}

const PLACEHOLDER_BUILDERS: Record<string, (opts: SupplementMissingRowsOpts) => ResultRow[]> = {
  [AGGREGATION_STANDARD]: buildStandardPlaceholders,
  [AGGREGATION_GRADE]: buildGradePlaceholders,
  [AGGREGATION_TEACHER]: buildTeacherPlaceholders,
}

// Student aggregation has no fixed universe to supplement with placeholders.
export const supplementMissingRows = (opts: SupplementMissingRowsOpts): ResultRow[] => supplementRowsWithPlaceholders(opts, PLACEHOLDER_BUILDERS)
